import { useFlag } from '@calendly/feature-check-react';
import { Fieldset, FieldError, FieldLabel, useErrorId } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { Select, Control, Options, Option } from '@calendly/ui/components/select';
import classNames from 'classnames';
import { useMemo, forwardRef } from 'react';
import { ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE } from 'constants/feature_flags';
import { useIsSmsDeactivated } from 'features/organization_settings';
const emailToInviteeType = 'booking.email.event_to_invitee';
const emailToPublisherType = 'booking.email.event_to_publisher';
const emailToSomeoneElseType = 'booking.email.event_to_someone_else';
const smsToInviteeType = 'booking.sms.event_to_invitee';
const smsToPublisherType = 'booking.sms.event_to_publisher';
const actionDetailsI18n = {
  [emailToInviteeType]: {
    title: () => I18n.t('publisher.workflows.actions.booking.email.event_to_invitee.title'),
    description: () => I18n.t('publisher.workflows.actions.booking.email.event_to_invitee.description')
  },
  [emailToSomeoneElseType]: {
    title: () => I18n.t('publisher.workflows.actions.booking.email.event_to_someone_else.title'),
    description: () => I18n.t('publisher.workflows.actions.booking.email.event_to_someone_else.description')
  },
  [emailToPublisherType]: {
    title: () => I18n.t('publisher.workflows.actions.booking.email.event_to_publisher.title'),
    description: () => I18n.t('publisher.workflows.actions.booking.email.event_to_publisher.description')
  },
  [smsToInviteeType]: {
    title: () => I18n.t('publisher.workflows.actions.booking.sms.event_to_invitee.title'),
    description: () => I18n.t('publisher.workflows.actions.booking.sms.event_to_invitee.description')
  },
  [smsToPublisherType]: {
    title: () => I18n.t('publisher.workflows.actions.booking.sms.event_to_publisher.title'),
    description: () => I18n.t('publisher.workflows.actions.booking.sms.event_to_publisher.description')
  }
};
const actionTitleCss = "ax9t7mg";
const ActionTitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionTitleCss, props.className)} />;
});
const actionDescriptionCss = "a1xa85um";
const ActionDescription = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionDescriptionCss, props.className)} />;
});
const badgeCss = "b6gmbdq";
const Badge = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(badgeCss, props.className)} />;
});
const deactivatedBadgeCss = "d1p0xttk";
const DeactivatedBadge = forwardRef((props, ref) => <Badge ref={ref} {...props} className={classNames(deactivatedBadgeCss, props.className)}>
    {I18n.t('publisher.enforcement.sms_deactivated')}
  </Badge>);
const workflowsDeactivatedBadgeCss = "w16kc8ac";
const WorkflowsDeactivatedBadge = forwardRef((props, ref) => <Badge ref={ref} {...props} className={classNames(workflowsDeactivatedBadgeCss, props.className)}>
    {I18n.t('publisher.workflows.actions.one_time_action')}
  </Badge>);
const ActionDetails = ({
  type
}) => <>
    <ActionTitle>
      {actionDetailsI18n[type].title()}
    </ActionTitle>
    <ActionDescription>
      {actionDetailsI18n[type].description()}
    </ActionDescription>
  </>;
const EmailOption = ({
  type
}) => <Option value={type}>
    <ActionDetails type={type} />
  </Option>;
const DeactivatedOption = ({
  type,
  badge: BadgeComponent = DeactivatedBadge
}) => <Option value={type} disabled>
    <ActionDetails type={type} />
    <BadgeComponent />
  </Option>;
const EmailToSomeoneElseDeactivatedOption = () => <DeactivatedOption type={emailToSomeoneElseType} badge={WorkflowsDeactivatedBadge} />;
const SmsOption = ({
  type
}) => {
  const smsDeactivated = useIsSmsDeactivated();
  if (smsDeactivated) {
    return <DeactivatedOption type={type} />;
  }
  return <EmailOption type={type} />;
};
const EmailToInvitee = () => <EmailOption type={emailToInviteeType} />;
const EmailToSomeoneElse = ({
  isActionAlreadyAssociated
}) => isActionAlreadyAssociated ? <EmailToSomeoneElseDeactivatedOption /> : <EmailOption type={emailToSomeoneElseType} />;
const EmailToHost = () => <EmailOption type={emailToPublisherType} />;
const SmsToInvitee = () => <SmsOption type={smsToInviteeType} />;
const SmsToPublisher = () => <SmsOption type={smsToPublisherType} />;
const formFieldId = 'doThis';
const selectButtonId = 'doThisControl';
export const ActionSelect = ({
  visibleActions = [],
  value,
  onChange,
  disabled,
  invalid,
  error
}) => {
  const errorId = useErrorId({
    error
  });
  const workflowActionEmailSomeoneElseEnabled = useFlag(ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE);
  const isWorkflowActionEmailSomeoneElseVisible = useMemo(() => visibleActions.some(action => action.configuration.type === emailToSomeoneElseType), [visibleActions]);
  return <Fieldset>
      <FieldLabel id={formFieldId} text={I18n.t('publisher.workflows.create.do_this')} />
      <Select value={value} onChange={onChange}>
        <Control id={selectButtonId} aria-labelledby={formFieldId} aria-describedby={errorId} disabled={disabled} invalid={invalid} aria-invalid={invalid} placeholder={I18n.t('publisher.workflows.forms.select')}>
          {value && actionDetailsI18n[value].title()}
        </Control>
        <Options>
          <EmailToInvitee />
          <EmailToHost />
          {workflowActionEmailSomeoneElseEnabled && <EmailToSomeoneElse isActionAlreadyAssociated={isWorkflowActionEmailSomeoneElseVisible} />}
          <SmsToInvitee />
          <SmsToPublisher />
        </Options>
      </Select>
      <FieldError text={error} id={errorId} />
    </Fieldset>;
};

require("./action_select.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./action_select.js");